import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { AuthRequestModel } from '../models/core/auth-request.model';
import { AuthResponseModel } from '../models/core/auth-response.model';
import { AuthResource } from './resources/auth.resource';
import { RolesResource } from './resources/roles.resource';
import { UsersResource } from './resources/users.resource';
import { SharedBusinessLogicService } from './shared-business-logic.service';
import { AppSessionStorage } from './storage/app.session-storage';
import { SecurityStorage } from './storage/security.storage';
import { SettingsSessionStorage } from './storage/settings.session-storage';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly router = inject(Router);
    private readonly securityStorage = inject(SecurityStorage);
    private readonly appSessionStorage = inject(AppSessionStorage);
    private readonly settingsSessionStorage = inject(SettingsSessionStorage);
    private readonly authResource = inject(AuthResource);
    private readonly sharedBusinessLogicService = inject(SharedBusinessLogicService);
    private readonly usersResource = inject(UsersResource);
    private readonly rolesResource = inject(RolesResource);

    login(data: AuthRequestModel): void {
        this.authResource.login(data).subscribe(response => {
            this.loadInitialData(response.value).subscribe(() => this.redirectFromLogin());
        });
    }

    logout(shouldCheckoutFromMeeting = false): void {
        if (shouldCheckoutFromMeeting) {
            this.sharedBusinessLogicService.checkOutFromMeeting();
        }
        this.settingsSessionStorage.eraseUserSettings();
        this.securityStorage.eraseSecurityData();

        if (environment.useAirlock) {
            window.location.href = `https://${window.location.hostname}/auth/ui/app/auth/logout`;
        } else {
            this.router.navigate(['login']).then();
        }
    }

    isLoggedIn(): Observable<boolean> {
        return this.authResource.authCheck().pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }

    requireUserToBeLoggedIn(): Observable<boolean> {
        return this.isLoggedIn().pipe(
            mergeMap(isLoggedIn => {
                if (isLoggedIn && !!this.securityStorage.getUserInfo() && this.securityStorage.getPermissions().length > 0) {
                    return of(true);
                }

                return this.usersResource.getUserInfo().pipe(
                    mergeMap(resp => {
                        if (!!resp) {
                            return this.loadInitialData(resp.value).pipe(map(() => true));
                        }

                        return of(false);
                    }),
                    catchError(() => of(false))
                );
            })
        );
    }

    /**
     * Checks whether the user has the informed permission.
     * @param name Permission.
     */
    hasPermission(name: string): boolean {
        const permissions = this.securityStorage.getPermissions();
        const existingPermission = permissions.find(p => p.name === name);

        return existingPermission?.isGranted ?? false;
    }

    /**
     * Checks whether the user has any of the informed permissions.
     * @param names Permissions.
     */
    hasAnyPermission(names: string[]): boolean {
        const permissions = this.securityStorage.getPermissions();
        return permissions.some(p => names.includes(p.name) && p.isGranted);
    }

    private redirectFromLogin(): void {
        let redirectRoute = this.appSessionStorage.getNotLoggedRedirectUrl() || 'home';
        redirectRoute = redirectRoute?.endsWith('login') ? 'home' : redirectRoute;

        if (redirectRoute !== 'home') {
            this.appSessionStorage.saveNotLoggedRedirectUrl(undefined);
        }

        redirectRoute = decodeURI(redirectRoute);
        window.location.href = redirectRoute;
    }

    private loadInitialData(authData: AuthResponseModel): Observable<void> {
        this.securityStorage.saveUserInfo(authData);
        return this.rolesResource.getCurrentPermissions().pipe(map(r => this.securityStorage.savePermissions(r.value)));
    }
}
